/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import { z } from 'zod';
import type { TFunction } from 'i18next';
import { CATEGORY_LABELS, PROPERTY_FIELD_LIMITS, maxYearBuilt } from './schema.constants';
import { MAX_PROPERTY_VIDEOS } from '@/shared/utils/property-videos';

export {
  PROPERTY_CATEGORIES,
  CATEGORY_LABELS,
  RESIDENTIAL_CATEGORIES,
  APARTMENT_CATEGORIES,
  RURAL_CATEGORIES,
  LAND_CATEGORIES,
  NO_CONSTRUCTED_AREA_CATEGORIES,
  TRANSACTION_TYPES,
  SHORT_TERM_TRANSACTION,
  AUCTION_TRANSACTION,
  PROPERTY_CONDITIONS,
  OCCUPANCIES,
  FINISH_STANDARDS,
  LOCATION_QUALITIES,
  YES_NO_OPTIONS,
  LEISURE_AREAS,
  FACILITIES,
  SECURITY_ITEMS,
  INFRASTRUCTURE_ITEMS,
  CONDO_AMENITIES,
  CONDO_SECURITY,
  MANDATE_TYPES,
  FURNISHED_OPTIONS,
  FACING_OPTIONS,
  PARKING_TYPE_OPTIONS,
  RENTAL_PAYMENT_FREQUENCY_OPTIONS,
  RENTAL_INCLUDED_EXPENSES_OPTIONS,
  ADMINISTRATION_TYPE_OPTIONS,
  CONDO_INCLUDED_EXPENSES_OPTIONS,
  CONDO_PAID_BY_OPTIONS,
  PROPERTY_FIELD_LIMITS,
  maxYearBuilt,
} from './schema.constants';
import { agencyRequiredRefinement, type AgencyRequiredRule } from './schema.agency-required';

export { getCategoryConfig } from './schema.category-config';

const CATEGORY_TO_KEY: Record<string, string> = {
  'Casa': 'house',
  'Apartamento': 'apartment',
  'Terreno': 'land',
  'Prédio Comercial': 'commercial',
  'Sala Comercial': 'commercialRoom',
  'Galpão': 'warehouse',
  'Kitnet': 'kitnet',
  'Studio': 'studio',
  'Flat': 'flat',
  'Loft': 'loft',
  'Cobertura': 'cobertura',
  'Chácara': 'chacara',
  'Casa Comercial': 'casaComercial',
  'Ponto Comercial': 'pontoComercial',
  'Garagem': 'garagem',
  'Fazenda': 'fazenda',
  'Sítio': 'sitio',
  'Pousada': 'pousada',
  'Academia': 'academia',
  'Edifício': 'edificio',
};

/**
 * Normalizes a category value to a lookup key by stripping accents and
 * underscores and lowercasing.
 *
 * @param raw - The raw category value (accented or underscored).
 * @returns The normalized lookup key.
 */
function normalizeCategoryKey(raw: string): string {
  return raw
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/_/g, ' ')
    .toLowerCase()
    .trim();
}

const NORMALIZED_CATEGORY_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(CATEGORY_TO_KEY).map(([k, v]) => [normalizeCategoryKey(k), v]),
);

/**
 * Resolves the localized label for a property category.
 * Normalizes input (accent/underscore/case-insensitive) for migration safety.
 */
export function getCategoryLabel(value: string, t?: (key: string) => string): string {
  if (!value) return '';
  const key = CATEGORY_TO_KEY[value] ?? NORMALIZED_CATEGORY_MAP[normalizeCategoryKey(value)];
  if (t && key) return t(`form.categories.${key}`);
  return CATEGORY_LABELS[value] || value;
}

/**
 * Resolves the localized label for a transaction type.
 */
export function getTransactionLabel(value: string, t?: TFunction): string {
  if (t) {
    const map: Record<string, string> = {
      'Venda': 'sale', 'Aluguel': 'rental', 'Permuta': 'exchange', 'Temporada': 'shortTerm', 'Leilão': 'auction',
    };
    const key = map[value];
    if (key) return t(`form.transactionTypes.${key}`);
  }
  return value;
}

/**
 * Builds a required-string zod field that accepts nullish values and numeric
 * API payloads (e.g. address number) by coercing them to strings first.
 */
function requiredString(message: string, maxLength?: number) {
  const base = maxLength === undefined
    ? z.string().min(1, message)
    : z.string().min(1, message).max(maxLength);
  return z.preprocess(
    (value) => (value == null ? '' : String(value)),
    base,
  );
}

/**
 * Builds an optional-string zod field that accepts nullish values and numeric
 * API payloads, coercing them to strings first (mirrors {@link requiredString}
 * without the non-empty constraint).
 */
function optionalString(maxLength?: number) {
  const base = maxLength === undefined
    ? z.string()
    : z.string().max(maxLength);
  return z.preprocess(
    (value) => (value == null ? '' : String(value)),
    base,
  );
}

const PROPERTY_CODE_PATTERN = /^[A-Za-z0-9._/-]*$/;

/**
 * Names the format problem of an agency property code, if it has one: up to 20
 * characters of letters, numbers and `. _ / -`. Not unique on purpose: an
 * agency may reuse a code on two keys. Mirrors `validate_agency_code` in the
 * backend.
 *
 * @param code - The code as typed.
 * @param messages - The message for a code that is too long, and for one with a character that is not allowed.
 * @returns The message of the problem, or undefined when the code is valid.
 */
export function propertyCodeProblem(
  code: string,
  messages: { max: string; invalid: string },
): string | undefined {
  const trimmed = code.trim();
  if (trimmed.length > PROPERTY_FIELD_LIMITS.propertyCode.maxLength) return messages.max;
  return PROPERTY_CODE_PATTERN.test(trimmed) ? undefined : messages.invalid;
}

/**
 * Builds the form-level check of the agency property code.
 *
 * Only a code the broker changed is checked: the form opens pre-filled with
 * whatever a spreadsheet import stored ("Apto 12", a 25-character reference),
 * and that must not block saving an unrelated edit.
 *
 * @param messages - The message for a code that is too long, and for one with a character that is not allowed.
 * @returns A zod `superRefine` callback.
 */
function propertyCodeRefinement(messages: { max: string; invalid: string }) {
  return (
    values: { property_code?: string; property_code_initial?: string },
    ctx: z.RefinementCtx,
  ) => {
    const code = (values.property_code ?? '').trim();
    if (code === (values.property_code_initial ?? '').trim()) return;
    const message = propertyCodeProblem(code, messages);
    if (message) ctx.addIssue({ code: 'custom', path: ['property_code'], message });
  };
}

interface AttributeFieldMessages {
  negativeMsg: string;
  moneyMax: number;
  moneyMaxMsg: string;
  quantityMaxMsg: string;
}

/**
 * Zod fields of the Univen attributes (plan §3.1): every one optional, an
 * empty value meaning "not informed".
 *
 * @param messages - Shared validation messages and the money ceiling.
 * @returns The field map to spread into the form schema.
 */
function attributeFields({ negativeMsg, moneyMax, moneyMaxMsg, quantityMaxMsg }: AttributeFieldMessages) {
  const money = () => z.coerce.number().min(0, negativeMsg).max(moneyMax, moneyMaxMsg).optional();
  const text = () => z.string().max(120).optional();
  return {
    condominium_name: text(),
    tower_name: text(),
    development_name: text(),
    developer: text(),
    distance_to_sea_m: z.coerce.number().int().min(0, negativeMsg).max(999999).optional(),
    block: z.string().max(30).optional(),
    unit: z.string().max(30).optional(),
    quadra: z.string().max(30).optional(),
    lot: z.string().max(30).optional(),
    short_term_price: money(),
    short_term_guests: z.coerce.number().int().min(0, negativeMsg).max(999, quantityMaxMsg).optional(),
    short_term_cleaning_fee: money(),
    auction_first_date: z.string().optional(),
    auction_first_price: money(),
    auction_second_date: z.string().optional(),
    auction_second_price: money(),
    condition: z.string().optional(),
    occupancy: z.string().optional(),
    finish_standard: z.string().optional(),
    location_quality: z.string().optional(),
    exclusive: z.string().optional(),
    exclusive_until: z.string().optional(),
    publish_authorized: z.string().optional(),
    publish_authorized_until: z.string().optional(),
    key_location: text(),
    partner_name: text(),
  };
}

/**
 * Builds the zod validation schema for the property form.
 *
 * @param t - Translator for the messages; Portuguese fallbacks without it.
 * @param agencyRule - The fields the agency requires per transaction, from
 *   Configurações > Imóveis. Omitted, only the form's own floor applies.
 */
export function createPropertyFormSchema(t?: TFunction, agencyRule?: AgencyRequiredRule) {
  /**
   * Resolves a validation message via i18n, falling back to a default.
   *
   * @param key - The validation message key.
   * @param fallback - The default message when no translator is provided.
   * @returns The resolved validation message.
   */
  const msg = (key: string, fallback: string) => t ? t(`form.validation.${key}`) : fallback;
  const negativeMsg = t ? t('form.validation.cannotBeNegative', 'Value cannot be negative') : 'Valor não pode ser negativo';
  const maxMsg = t ? t('form.validation.max100', 'Maximum 100%') : 'Máximo 100%';
  const yearRangeMsg = msg('yearBuiltRange', 'Informe um ano entre 1500 e o ano atual');
  const roomsMaxMsg = msg('totalRoomsMax', 'Máximo 99 cômodos');
  const floorMaxMsg = msg('floorMax', 'Máximo 200 andares');
  const quantityMaxMsg = msg('quantityMax', 'Máximo 20');
  const leaseMaxMsg = msg('leaseTermMax', 'Máximo 600 meses');
  const registrationMaxMsg = msg('registrationNumberMax', 'Máximo 30 caracteres');
  const registryMaxMsg = msg('registryOfficeMax', 'Máximo 120 caracteres');
  const inscriptionMaxMsg = msg('inscriptionNumberMax', 'Máximo 30 caracteres');
  const descriptionMaxMsg = msg('descriptionMax', 'Máximo 5000 caracteres');
  const moneyMaxMsg = msg('moneyMax', 'Valor acima do limite permitido');
  const areaMaxMsg = msg('areaMax', 'Área acima do limite permitido');
  const qty = PROPERTY_FIELD_LIMITS.quantity;
  const moneyMax = 10 ** PROPERTY_FIELD_LIMITS.money.maxDigits / 100 - 0.01;
  const areaMax = 10 ** PROPERTY_FIELD_LIMITS.area.maxDigits / 100 - 0.01;

  return z.object({
    category: requiredString(msg('categoryRequired', 'Selecione o tipo de imóvel')),
    // Optional since 2026-10-28 (plan §12.3): a listing may be registered
    // before the agency knows how it will be offered.
    transaction_type: z.array(z.string()),

    postal_code: requiredString(msg('postalCodeRequired', 'CEP é obrigatório')),
    street: requiredString(msg('streetRequired', 'Rua é obrigatória'), PROPERTY_FIELD_LIMITS.street.maxLength),
    // Optional: plenty of real addresses have no house number (rural lots,
    // "S/N" frontages), so requiring one blocked legitimate registrations.
    number: optionalString(PROPERTY_FIELD_LIMITS.number.maxLength),
    complement: z.string().max(PROPERTY_FIELD_LIMITS.complement.maxLength).optional(),
    neighborhood: requiredString(msg('neighborhoodRequired', 'Bairro é obrigatório'), PROPERTY_FIELD_LIMITS.neighborhood.maxLength),
    city: requiredString(msg('cityRequired', 'Cidade é obrigatória'), PROPERTY_FIELD_LIMITS.city.maxLength),
    state: requiredString(msg('stateRequired', 'Estado é obrigatório')),

    total_area: z.coerce.number().min(0, negativeMsg).max(areaMax, areaMaxMsg).optional(),
    constructed_area: z.coerce.number().min(0, negativeMsg).max(areaMax, areaMaxMsg).optional(),
    sale_price: z.coerce.number().min(0, negativeMsg).max(moneyMax, moneyMaxMsg).optional(),
    rental_price: z.coerce.number().min(0, negativeMsg).max(moneyMax, moneyMaxMsg).optional(),
    condominium_price: z.coerce.number().min(0, negativeMsg).max(moneyMax, moneyMaxMsg).optional(),
    iptu: z.coerce.number().min(0, negativeMsg).max(moneyMax, moneyMaxMsg).optional(),

    bedrooms: z.coerce.number().int().min(qty.min).max(qty.max, quantityMaxMsg).optional(),
    suites: z.coerce.number().int().min(qty.min).max(qty.max, quantityMaxMsg).optional(),
    bathrooms: z.coerce.number().int().min(qty.min).max(qty.max, quantityMaxMsg).optional(),
    parking_spots: z.coerce.number().int().min(qty.min).max(qty.max, quantityMaxMsg).optional(),
    floor: z.coerce.number().int().min(PROPERTY_FIELD_LIMITS.floor.min).max(PROPERTY_FIELD_LIMITS.floor.max, floorMaxMsg).optional(),
    fixed_parking: z.boolean().optional(),
    gourmet_balcony: z.boolean().optional(),
    balcony: z.boolean().optional(),
    accepts_pets: z.boolean().optional(),
    furnished: z.string().optional(),
    facing: z.string().optional(),
    year_built: z.coerce.number().int()
      .min(PROPERTY_FIELD_LIMITS.yearBuilt.min, yearRangeMsg)
      .max(maxYearBuilt(), yearRangeMsg)
      .optional(),
    parking_type: z.string().optional(),
    total_rooms: z.coerce.number().int()
      .min(PROPERTY_FIELD_LIMITS.totalRooms.min)
      .max(PROPERTY_FIELD_LIMITS.totalRooms.max, roomsMaxMsg)
      .optional(),
    accepts_exchange: z.boolean().optional(),
    rental_payment_frequency: z.string().optional(),
    rental_included_expenses: z.array(z.string()).optional(),
    administration_type: z.string().optional(),
    condo_included_expenses: z.array(z.string()).optional(),
    condo_paid_by: z.string().optional(),

    leisure_areas: z.array(z.string()).optional(),
    facilities: z.array(z.string()).optional(),
    security: z.array(z.string()).optional(),
    infrastructure: z.array(z.string()).optional(),
    highlights: z.array(z.string()).optional(),

    condominium: z.boolean().optional(),
    condo_amenities: z.array(z.string()).optional(),
    condo_security: z.array(z.string()).optional(),

    rental_guarantee: z.array(z.string()).optional(),
    admin_percentage: z.coerce.number().min(0, negativeMsg).max(100, maxMsg).optional(),
    lease_term_in_months: z.coerce.number().int()
      .min(PROPERTY_FIELD_LIMITS.leaseTermMonths.min, negativeMsg)
      .max(PROPERTY_FIELD_LIMITS.leaseTermMonths.max, leaseMaxMsg)
      .optional(),

    tags: z.array(z.string()).optional(),
    property_title: requiredString(
      msg('titleRequired', 'Título do anúncio é obrigatório'),
      PROPERTY_FIELD_LIMITS.propertyTitle.maxLength,
    ),
    description: z.string().max(PROPERTY_FIELD_LIMITS.description.maxLength, descriptionMaxMsg).optional(),

    property_code: optionalString(),
    // The code the form opened with; never edited, only compared against on save.
    property_code_initial: z.string().optional(),

    virtual_tour_url: z.string().optional(),
    video_links: z.array(z.string()).max(MAX_PROPERTY_VIDEOS).optional(),

    registration_number: z.string()
      .max(PROPERTY_FIELD_LIMITS.registrationNumber.maxLength, registrationMaxMsg)
      .optional(),
    registry_office: z.string()
      .max(PROPERTY_FIELD_LIMITS.registryOffice.maxLength, registryMaxMsg)
      .optional(),
    inscription_number: z.string()
      .max(PROPERTY_FIELD_LIMITS.inscriptionNumber.maxLength, inscriptionMaxMsg)
      .optional(),

    owner_client_id: z.string().optional(),
    mandate_type: z.string().optional(),
    mandate_start_date: z.string().optional(),
    mandate_end_date: z.string().optional(),
    mandate_commission: z.coerce.number().min(0, negativeMsg).max(100, maxMsg).optional(),

    primary_broker: z.string().optional(),
    captured_by: z.string().optional(),
    assigned_brokers: z.array(z.string()).optional(),

    ...attributeFields({ negativeMsg, moneyMax, moneyMaxMsg, quantityMaxMsg }),
  }).superRefine(propertyCodeRefinement({
    max: msg('codeMax', 'Máximo 20 caracteres'),
    invalid: msg('codeInvalid', 'Use apenas letras, números e . _ / -'),
  })).superRefine((values, ctx) => {
    if (agencyRule) agencyRequiredRefinement(agencyRule)(values, ctx);
  });
}

export const propertyFormSchema = createPropertyFormSchema();

export type PropertyFormValues = z.infer<typeof propertyFormSchema>;

export { DEFAULT_VALUES } from './schema.defaults';
